import React, { useContext, useMemo } from 'react';

import { ConstructorGridType } from '@/interfaces/constructor.interfaces';
import GridUtils from '@/utils/gridUtils';
import instanceOfGrid from '@/utils/instanceOfGrid';

interface IConstructorContext {
  grid: ConstructorGridType;
}

export const ConstructorContext = React.createContext<IConstructorContext>({
  grid: {
    id: 'root',
    items: [],
  },
});

export function useConstructor<Grid extends ConstructorGridType>(
  condition?: (grid: ConstructorGridType) => boolean,
  defaultGrid?: Grid,
) {
  const context = useContext(ConstructorContext);

  if (context === undefined) {
    throw new Error(
      `${useConstructor.name} hook was used outside of ${ConstructorContext.Provider.name}.`,
    );
  }

  const grid = useMemo(() => {
    if (!condition) {
      return context.grid as Grid;
    }

    const foundGrid = GridUtils.findByCondition(context.grid, element => {
      if (!instanceOfGrid(element)) {
        return false;
      }

      return condition(element);
    });

    return (foundGrid ?? defaultGrid) as Grid;
  }, [condition, context.grid, defaultGrid]);

  return {
    ...context,
    grid,
  };
}
